import { ClipboardDocumentCheckIcon } from "@heroicons/react/24/outline";
import { ChevronRightIcon } from "@heroicons/react/20/solid";
import { cn } from "../utils/cn.js";
import { useIsGummy } from "../hooks/useGummyTheme.js";

type ChecklistData = {
  id: number | string;
  title?: string | null;
  description?: string | null;
  item_count?: number;
  completed_count?: number;
};

type ChecklistCardProps = {
  checklist: ChecklistData;
  className?: string;
  onClick?: () => void;
};

const getProgress = (checklist: ChecklistData): number | null => {
  if (
    typeof checklist.item_count !== "number" ||
    typeof checklist.completed_count !== "number" ||
    checklist.item_count === 0
  ) {
    return null;
  }
  return Math.round((checklist.completed_count / checklist.item_count) * 100);
};

const ChecklistCard = ({
  checklist,
  className,
  onClick
}: ChecklistCardProps) => {
  const isGummy = useIsGummy();
  const title = checklist.title || "Untitled";
  const progress = getProgress(checklist);
  const Component = onClick ? "button" : "div";

  if (isGummy) {
    return (
      <Component
        onClick={onClick}
        className={cn(
          "shadow-card",
          "group relative flex flex-col rounded-surface-lg bg-white dark:bg-mono-900 p-6 text-left",
          "transition-transform duration-150 hover:-translate-y-0.5 active:scale-[0.98]",
          "motion-reduce:transform-none motion-reduce:transition-none",
          "outline-none focus-visible:ring-4 focus-visible:ring-primary-400/40",
          onClick && "cursor-pointer",
          className
        )}
      >
        <div className="flex items-center gap-4">
          <div
            className={cn(
              "flex h-14 w-14 shrink-0 items-center justify-center rounded-surface-sm text-white",
              "bg-tile-success",
              "shadow-raised text-shadow-on-color-strong"
            )}
          >
            <ClipboardDocumentCheckIcon className="size-7" strokeWidth={2.5} />
          </div>
          <div className="min-w-0 flex-1">
            <h5 className="truncate text-xl font-black tracking-tight text-mono-900 dark:text-white">
              {title}
            </h5>
            {progress !== null && (
              <p className="mt-1.5 text-xs font-black uppercase tracking-widest text-mono-600 dark:text-mono-300">
                {checklist.completed_count} of {checklist.item_count} items
              </p>
            )}
          </div>
          <ChevronRightIcon className="size-7 shrink-0 text-mono-500 transition-transform group-hover:translate-x-0.5 dark:text-mono-300" />
        </div>

        {progress !== null && (
          <div className="mt-5">
            <div
              className={cn(
                "relative h-5 w-full overflow-hidden rounded-full",
                "shadow-sunken",
                "bg-white/60 dark:bg-mono-900"
              )}
            >
              <div
                className={cn(
                  "h-full rounded-full bg-tile-success",
                  "shadow-[inset_0_1px_0_0_rgba(255,255,255,0.5)]",
                  "transition-[width] duration-300"
                )}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
      </Component>
    );
  }

  return (
    <Component
      onClick={onClick}
      className={cn(
        "border-mono-200 hover:border-mono-300 dark:border-mono-700/50 dark:bg-mono-800/50 dark:hover:border-mono-600 group relative flex flex-col rounded-xl border bg-white p-4 text-left transition-all hover:shadow-md",
        onClick && "cursor-pointer",
        className
      )}
    >
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-500 dark:bg-emerald-950 dark:text-emerald-400">
          <ClipboardDocumentCheckIcon className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h5 className="text-mono-900 dark:text-mono-100 truncate text-sm font-medium">
            {title}
          </h5>
          {progress !== null && (
            <p className="text-mono-500 dark:text-mono-400 mt-0.5 text-xs">
              {checklist.completed_count} of {checklist.item_count} items
            </p>
          )}
        </div>
        <ChevronRightIcon className="text-mono-300 group-hover:text-mono-500 dark:text-mono-600 dark:group-hover:text-mono-400 h-5 w-5 shrink-0 transition-transform group-hover:translate-x-0.5" />
      </div>

      {progress !== null && (
        <div className="mt-3">
          <div className="bg-mono-100 dark:bg-mono-700 h-1.5 w-full overflow-hidden rounded-full">
            <div
              className={cn(
                "h-full rounded-full transition-all",
                progress === 100
                  ? "bg-emerald-500"
                  : "bg-emerald-400 dark:bg-emerald-500"
              )}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}
    </Component>
  );
};

const ChecklistCardSkeleton = () => (
  <div className="border-mono-200 dark:border-mono-700/50 dark:bg-mono-800/50 flex animate-pulse flex-col rounded-xl border bg-white p-4">
    <div className="flex items-center gap-3">
      <div className="bg-mono-200 dark:bg-mono-700 h-9 w-9 shrink-0 rounded-lg" />
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="bg-mono-200 dark:bg-mono-700 h-4 w-3/4 rounded" />
        <div className="bg-mono-200 dark:bg-mono-700 h-3 w-1/3 rounded" />
      </div>
      <div className="bg-mono-200 dark:bg-mono-700 h-5 w-5 rounded" />
    </div>
    <div className="mt-3">
      <div className="bg-mono-100 dark:bg-mono-700 h-1.5 w-full rounded-full" />
    </div>
  </div>
);

export { ChecklistCard, ChecklistCardSkeleton };
export type { ChecklistData, ChecklistCardProps };
